import { formatMoney } from '../core/math';
import { INDEX } from '../game/config';
import type { Game } from '../game/Game';
import { h, setAttr, setText, toggleClass } from './dom';

/** Top bar: clock, the Waffle House Index, tips, and active multipliers. */
export class Hud {
  readonly el: HTMLElement;
  private readonly time: HTMLElement;
  private readonly ampm: HTMLElement;
  private readonly index: HTMLElement;
  private readonly indexValue: HTMLElement;
  private readonly indexFill: HTMLElement;
  private readonly indexMenu: HTMLElement;
  private readonly tips: HTMLElement;
  private readonly chips: HTMLElement;
  readonly pauseButton: HTMLButtonElement;
  readonly muteButton: HTMLButtonElement;
  private lastTips = -1;
  private chipKey = '';

  constructor() {
    this.time = h('span', { class: 'hud__time' }, '10:00');
    this.ampm = h('span', { class: 'hud__ampm' }, 'PM');
    this.indexValue = h('span', { class: 'hud__index-value' }, 'GREEN');
    this.indexFill = h('div', { class: 'hud__index-fill' });
    this.indexMenu = h('span', { class: 'hud__index-menu' }, 'FULL MENU');
    this.index = h(
      'div',
      {
        class: 'hud__index',
        'data-level': 'green',
        role: 'meter',
        'aria-label': 'Waffle House Index',
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-valuenow': 100,
      },
      h('div', { class: 'hud__index-label' }, 'WAFFLE HOUSE INDEX'),
      h(
        'div',
        { class: 'hud__index-row' },
        this.indexValue,
        h(
          'div',
          { class: 'hud__index-bar' },
          this.indexFill,
          h('div', { class: 'hud__index-mark', style: `left:${INDEX.greenAt}%` }),
        ),
      ),
      this.indexMenu,
    );
    this.tips = h('span', { class: 'hud__tips-value' }, '$0.00');
    this.chips = h('div', { class: 'hud__chips', 'aria-live': 'polite' });
    this.pauseButton = h(
      'button',
      { class: 'hud__btn', type: 'button', 'aria-label': 'Pause', title: 'Pause (Esc)' },
      '❚❚',
    );
    this.muteButton = h(
      'button',
      { class: 'hud__btn', type: 'button', 'aria-label': 'Toggle sound', title: 'Sound (M)' },
      '♪',
    );
    this.el = h(
      'header',
      { class: 'hud' },
      h('div', { class: 'hud__clock', 'aria-label': 'Shift clock' }, this.time, this.ampm),
      this.index,
      h('div', { class: 'hud__tips' }, h('span', { class: 'hud__tips-label' }, 'TIPS'), this.tips),
      this.chips,
      h('div', { class: 'hud__buttons' }, this.muteButton, this.pauseButton),
    );
  }

  setMuted(muted: boolean): void {
    setText(this.muteButton, muted ? '♪̸' : '♪');
    toggleClass(this.muteButton, 'is-off', muted);
  }

  update(game: Game): void {
    const [time = '', ampm = ''] = game.clock.split(' ');
    setText(this.time, time);
    setText(this.ampm, ampm);

    const level = game.level;
    setAttr(this.index, 'data-level', level);
    setAttr(this.index, 'aria-valuenow', String(Math.round(game.index)));
    toggleClass(this.index, 'is-critical', game.index < INDEX.criticalAt);
    setText(this.indexValue, level.toUpperCase());
    this.indexFill.style.transform = `scaleX(${Math.max(0, game.index) / 100})`;
    setText(
      this.indexMenu,
      !game.powerOn ? 'POWER OUT · LIMITED MENU' : level === 'green' ? 'FULL MENU' : 'LIMITED MENU',
    );

    if (game.tips !== this.lastTips) {
      const grew = game.tips > this.lastTips && this.lastTips >= 0;
      this.lastTips = game.tips;
      setText(this.tips, formatMoney(game.tips));
      if (grew) {
        this.tips.classList.remove('is-bump');
        void this.tips.offsetWidth;
        this.tips.classList.add('is-bump');
      }
    }

    const chips: [string, string][] = [];
    if (game.unbothered) chips.push(['unbothered', '😐 UNBOTHERED ×2']);
    if (game.live) chips.push(['live', '🔴 LIVE ×1.5']);
    if (game.combo >= 2) chips.push(['combo', `🔥 COMBO ×${game.combo}`]);
    if (!game.powerOn) chips.push(['power', '⚡ NO POWER']);
    if (game.eggSurcharge) chips.push(['egg', '🥚 +50¢']);
    const key = chips.map((c) => c[1]).join('|');
    if (key !== this.chipKey) {
      this.chipKey = key;
      this.chips.replaceChildren(
        ...chips.map(([cls, text]) => h('span', { class: `chip chip--${cls}` }, text)),
      );
    }
  }
}
